import React, { useEffect, useState, useMemo } from 'react'
import styled from 'styled-components'
import { useForm, useWatch } from 'react-hook-form'
import { useDispatch, useSelector } from 'react-redux'
import { useIntl } from 'react-intl'
import { Grid, Tooltip } from '@mui/material'
import InfoIcon from '@mui/icons-material/Info'

import InputField from 'components/form/InputField'
import GenericSelect from 'components/form/GenericSelect'
import SubmitButton from 'components/form/SubmitButton'
import SuccessMessage from 'components/ui/SuccessMessage'
import { FormattedMessageWrapper } from 'components/ui/FormattedMessageWrapper'

import { selectCourse } from 'store/course/slice'
import { editCourse, createCourse, generateInitialMapping } from 'store/course/thunks'
import { selectClub } from 'store/club/slice'
import CourseProviderVersionUpdate from './CourseProviderVersionUpdate'

interface Props {
  course: Course
  clubId: number | null
}

enum COURSE_STATUS {
  Official = 'true',
  Unofficial = 'false',
  Active = 'true',
  Inactive = 'false',
  GpsOfficial = 'true',
  GpsUnofficial = 'false',
}

export default function CourseBasicData({ course, clubId }: Props): JSX.Element {
  const [creatingNewCourse, setCreatingNewCourse] = useState<boolean>(false)

  const intl = useIntl()
  const dispatch = useDispatch()
  const form = useForm<CourseFormPayload>()
  const { handleSubmit, control, reset, register } = form
  const watch = useWatch({ control })
  const { saveStatus, initialMappingStatus, initialMappingError } = useSelector(selectCourse)
  const { club } = useSelector(selectClub)

  const noOfficialCourseVersion = useMemo(() => {
    const versions = course.versions || []
    const officialVersions = versions.filter((cv: CourseVersion) => cv.isOfficial)
    return officialVersions.length === 0
  }, [course])

  useEffect(() => {
    if (course) {
      reset({
        name: course.name,
        holeCount: course.holeCount ? String(course.holeCount) : undefined,
        isOfficial: course.isOfficial ? COURSE_STATUS.Official : COURSE_STATUS.Unofficial,
        isActive: course.isActive ? COURSE_STATUS.Active : COURSE_STATUS.Inactive,
        isGpsOfficial:
          course.officialGpsPoints && course.officialGpsPoints > 0
            ? COURSE_STATUS.GpsOfficial
            : COURSE_STATUS.GpsUnofficial,
      })
    }
  }, [course, reset])

  const officialOptions: SelectOption[] = [
    { key: COURSE_STATUS.Official, label: intl.formatMessage({ id: 'strings.official' }) },
    { key: COURSE_STATUS.Unofficial, label: intl.formatMessage({ id: 'strings.unofficial' }) },
  ]

  const yesNoOptions: SelectOption[] = [
    { key: COURSE_STATUS.Active, label: intl.formatMessage({ id: 'strings.yes' }) },
    { key: COURSE_STATUS.Inactive, label: intl.formatMessage({ id: 'strings.no' }) },
  ]

  const courseHasGpsPoints = () => {
    if (!course) {
      return '-'
    }
    return course.gps ? intl.formatMessage({ id: 'strings.yes' }) : intl.formatMessage({ id: 'strings.no' })
  }

  const getIsActiveValue = () => {
    const courseChangedToOfficial = watch['isOfficial'] === COURSE_STATUS.Official && course.isOfficial === false
    if (courseChangedToOfficial) {
      return COURSE_STATUS.Active
    }
    return watch['isActive']
  }

  const getClubName = () => {
    if (course.club?.name) {
      return course.club?.name
    }
    if (clubId && club.id > 0) {
      return clubId === club.id ? club.name : clubId
    }

    return clubId
  }

  const submitInitialMapping = () => {
    dispatch(generateInitialMapping({ courseId: course.id }))
  }

  const submitForm = (data: CourseFormPayload) => {
    setCreatingNewCourse(false)
    const isActive =
      data.isActive === COURSE_STATUS.Active ||
      (course.provider?.options?.disableSettingCourseUnofficial !== true && data.isOfficial === COURSE_STATUS.Official)
    const courseData: CourseDataPayload = {
      name: data.name,
      isOfficial: data.isOfficial === COURSE_STATUS.Official,
      isActive: isActive,
      holeCount: data.holeCount ? Number(data.holeCount) : 18,
    }

    if (course.id) {
      const payload: CoursePayload = {
        id: course.id,
        data: courseData,
      }
      dispatch(editCourse(payload))
      return
    }

    courseData['clubId'] = clubId || undefined
    dispatch(createCourse(courseData))
    setCreatingNewCourse(true)
  }

  const saveIsCompleted = () => {
    return creatingNewCourse
      ? saveStatus === 'succeeded' && initialMappingStatus === 'succeeded'
      : saveStatus === 'succeeded'
  }

  const getSaveStatus = () => {
    if (creatingNewCourse) {
      return saveStatus === 'succeeded' ? initialMappingStatus : saveStatus
    }
    return saveStatus
  }

  const maybeGetNoOfficialVersionTitle = () => {
    const courseNotOfficial = watch['isOfficial'] === COURSE_STATUS.Unofficial
    return noOfficialCourseVersion && courseNotOfficial
      ? intl.formatMessage({ id: 'strings.courseNeedsOfficialCourseVersionMessage' })
      : ''
  }

  const officialSelectShouldBeDisabled = (): boolean => {
    return (
      noOfficialCourseVersion ||
      (course.isOfficial === true && course.provider?.options?.disableSettingCourseUnofficial === true)
    )
  }

  const activeSelectShouldBeDisabled = (): boolean => {
    return (
      (watch['isOfficial'] === COURSE_STATUS.Official || noOfficialCourseVersion) &&
      course.provider?.options?.disableSettingCourseUnofficial !== true
    )
  }

  const renderTooltipContent = () => {
    return (
      <TooltipContent>
        <FormattedMessageWrapper id="strings.courseStatusTooltip" />
      </TooltipContent>
    )
  }

  const renderTooltip = () => {
    return (
      <Tooltip title={renderTooltipContent()} placement="bottom-start">
        <InfoIcon />
      </Tooltip>
    )
  }

  return (
    <form onSubmit={handleSubmit(submitForm)}>
      <BasicData container>
        <TopContainer>
          <SectionTitle>
            {course.id > 0
              ? intl.formatMessage({ id: 'strings.modifyCourse' })
              : intl.formatMessage({ id: 'strings.addCourse' })}
          </SectionTitle>
          <Grid
            sx={{
              display: 'flex',
              justifyContent: 'flex-end',
              flexDirection: 'column',
              alignContent: 'space-around',
              alignItems: 'flex-start',
            }}
          >
            <SubmitButton status={getSaveStatus()} text={intl.formatMessage({ id: 'strings.save' })} />
            <CourseProviderVersionUpdate course={course} />
          </Grid>
        </TopContainer>
        <MessageContainer item xs={8}>
          {initialMappingError && initialMappingStatus === 'failed' && (
            <>
              <SubmitButton
                status={initialMappingStatus}
                text={intl.formatMessage({ id: 'strings.retry' })}
                handleSubmit={submitInitialMapping}
              />
            </>
          )}
          {saveIsCompleted() && (
            <SuccessMessage>{intl.formatMessage({ id: 'strings.courseSavedSuccessfully' })}</SuccessMessage>
          )}
        </MessageContainer>
        <Grid item xs={5}>
          <FormTable>
            <tbody>
              <tr>
                <td>{intl.formatMessage({ id: 'strings.clubsName' })}</td>
                <td>{getClubName()}</td>
              </tr>
              <tr>
                <td>{intl.formatMessage({ id: 'strings.coursesName' })}</td>
                <td>
                  <InputField name="name" register={register} type="text" label="" outlined value={watch['name']} />
                </td>
              </tr>
              <tr>
                <td className="disabled">{intl.formatMessage({ id: 'strings.holes' })}</td>
                <td>
                  <InputField
                    name="holeCount"
                    register={register}
                    type="number"
                    label=""
                    outlined
                    disabled
                    value={watch['holeCount']}
                  />
                </td>
              </tr>
              {course?.club?.id && course.club.id > 0 && !course.club.timeZone && (
                <tr>
                  <td colSpan={2}>
                    <ValidTimeZone>{intl.formatMessage({ id: 'strings.validTimeZone' })}</ValidTimeZone>
                  </td>
                </tr>
              )}
              <tr>
                <td>{intl.formatMessage({ id: 'strings.provider' })}</td>
                <td>{course.provider?.abbreviation}</td>
              </tr>
            </tbody>
          </FormTable>
        </Grid>
        <Grid item xs={5}>
          <FormTable>
            <tbody>
              <tr>
                <td>{intl.formatMessage({ id: 'strings.status' })}</td>
                <td>
                  <OfficialSelect
                    name="isOfficial"
                    label=""
                    control={control}
                    options={officialOptions}
                    initialValue={watch['isOfficial']}
                    disabled={officialSelectShouldBeDisabled()}
                    title={maybeGetNoOfficialVersionTitle()}
                  />
                  {renderTooltip()}
                </td>
              </tr>
              <tr>
                <td>{intl.formatMessage({ id: 'strings.active' })}</td>
                <td>
                  <GenericSelect
                    name="isActive"
                    label=""
                    control={control}
                    options={yesNoOptions}
                    initialValue={getIsActiveValue()}
                    disabled={activeSelectShouldBeDisabled()}
                    title={maybeGetNoOfficialVersionTitle()}
                  />
                </td>
              </tr>
              <tr>
                <td>{intl.formatMessage({ id: 'strings.gps' })}</td>
                <td>
                  <CellContainer>
                    <span>{courseHasGpsPoints()}</span>
                    <GenericSelect
                      name="isGpsOfficial"
                      label=""
                      control={control}
                      options={officialOptions}
                      initialValue={watch['isGpsOfficial']}
                      disabled
                    />
                    {course.totalGpsPoints && course.totalGpsPoints > 0 ? (
                      <span>
                        {course.officialGpsPoints}/{course.totalGpsPoints}
                      </span>
                    ) : (
                      ''
                    )}
                  </CellContainer>
                </td>
              </tr>
            </tbody>
          </FormTable>
        </Grid>
      </BasicData>
    </form>
  )
}

const BasicData = styled(Grid)`
  padding: 0 20px;

  & .MuiFormControl-root {
    margin: 0;
    & .MuiInputBase-formControl {
      height: 40px;
      background-color: ${(props) => props.theme.customPalette.lightGray};
      width: 80%;
    }
  }
`

const TopContainer = styled.div`
  display: flex;
  width: 100%;
  justify-content: space-between;
`

const SectionTitle = styled.h2`
  width: 100%;
  font-style: italic;
  font-weight: 800;
  font-size: 20px;
  line-height: 27px;
  color: ${(props) => props.theme.customPalette.darkGray2};
  margin-bottom: 25px;
`

const FormTable = styled.table`
  width: 100%;
  & tr {
    height: 40px;
    & td {
      letter-spacing: 0.4px;
      vertical-align: middle;
      padding: 5px 0;

      &:first-of-type {
        padding-right: 20px;
        text-align: right;
        width: 100px;
      }

      &.disabled {
        opacity: 0.5;
      }
    }
  }
`

const CellContainer = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  width: calc(80% + 50px);

  & .MuiInputBase-formControl {
    width: 100% !important;
  }
  & > span {
    margin: 0 0 0 10px;

    &:first-of-type {
      margin: 0 10px 0 0;
    }
  }
`

const MessageContainer = styled(Grid)`
  margin: 20px 0;
`

const OfficialSelect = styled(GenericSelect)`
  display: inline;
  & ~ svg {
    margin: 8px 5px;
    color: ${(props) => props.theme.palette.primary.main};
  }
`

const TooltipContent = styled.div`
  border: none;
  box-shadow: none;
  padding: 10px 5px;
  width: 250px;
  background-color: #fff;
  color: red;
  padding: 10px;

  & > * {
    font-style: normal;
    font-size: 14px;
    line-height: 150%;
    color: #404040;
  }
  & > h5 {
    font-weight: 500;
  }
  & > p {
    font-weight: 400;
    margin-bottom: 10px;
  }
`

const ValidTimeZone = styled.span`
  float: left;
  padding: 5px;
  background-color: red;
  color: white;
`
